// src/features/task/useParentTitles.ts
// Resolves typed parent names for task rows and details. Entity references
// carry no title; the owning service supplies it when the caller can read it.

import { useEffect, useState } from 'react';

import { GetTask } from '@core/api/endpoints/task/get_task';
import { GetProject } from '@core/api/endpoints/project/get_project';
import { GetTeam } from '@core/api/endpoints/team/get_team';
import { GetDocument } from '@core/api/endpoints/document/get_document';

type ParentRef = {
  id?: string;
  type_name?: string;
  type_id?: string;
  title?: string;
  display_name?: string;
};

const cache = new Map<string, string>();
const pending = new Set<string>();
// Failed lookups should not turn a render into a request loop. A later render
// can retry after the gateway's one-minute rate-limit window has passed.
const retryAfter = new Map<string, number>();

export function taskParentIds(parents: ParentRef[]): string[] {
  return [...new Set(parents
    .filter((parent) => parent.type_name === 'task' || parent.type_id === 'task')
    .map((parent) => parent.id)
    .filter((id): id is string => !!id))];
}

function parentKind(parent: ParentRef): string {
  return parent.type_name || parent.type_id || '';
}

export async function resolveParentTitle(kind: string, id: string): Promise<string> {
  if (kind === 'task') {
    const response = await GetTask(id);
    return response.task?.title || '';
  }
  if (kind === 'project') {
    const response = await GetProject(id);
    return response.project?.name || '';
  }
  if (kind === 'team') {
    const response = await GetTeam(id);
    return response.team?.name || '';
  }
  if (kind === 'document') {
    const response = await GetDocument(id);
    return response.document?.title || '';
  }
  return '';
}

export function useParentTitles(parents: ParentRef[]): Record<string, string> {
  const [titles, setTitles] = useState<Record<string, string>>(() =>
    Object.fromEntries([...cache.entries()]),
  );

  const inlineTitles = Object.fromEntries(parents
    .filter((parent) => parent.id && (parent.title || parent.display_name))
    .map((parent) => [parent.id!, parent.title || parent.display_name!]));

  const missingKey = [...new Set(parents
    .filter((parent) => parent.id && !parent.title && !parent.display_name && ['task', 'project', 'team', 'document'].includes(parentKind(parent)))
    .map((parent) => `${parentKind(parent)}:${parent.id}`))]
    .filter((key) => !cache.has(key) && !pending.has(key) && Date.now() >= (retryAfter.get(key) ?? 0))
    .sort()
    .join(',');

  useEffect(() => {
    if (!missingKey) return;
    for (const key of missingKey.split(',')) {
      const [kind, id] = key.split(':');
      pending.add(key);
      resolveParentTitle(kind, id)
        .then((title) => {
          if (title) cache.set(key, title);
          else retryAfter.set(key, Date.now() + 60_000);
        })
        .catch(() => {
          retryAfter.set(key, Date.now() + 60_000);
          // The group header falls back to the short id.
        })
        .finally(() => {
          pending.delete(key);
          // No cancellation guard: StrictMode's double-invoke would otherwise
          // drop the completed fetch's state update, and a late setState on an
          // unmounted component is a no-op in React 18.
          setTitles(Object.fromEntries([...cache.entries()]));
        });
    }
  }, [missingKey]);

  const resolvedTitles = Object.fromEntries(parents
    .filter((parent) => parent.id)
    .map((parent) => [parent.id!, titles[`${parentKind(parent)}:${parent.id}`]])
    .filter((entry) => !!entry[1]));
  return { ...resolvedTitles, ...inlineTitles };
}
